import { Dispatch, FC, SetStateAction, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faThumbsDown, faThumbsUp, faXmark } from '@fortawesome/free-solid-svg-icons';
import * as assistantService from 'services/assistant';
import type { ChatMessage } from 'utils/assistantChatStore';

const MAX_TEXT_CHARS = 500;
const POPOVER_WIDTH = 320;
const POPOVER_ESTIMATED_HEIGHT = 360;
const EDGE_GAP = 8;
const THANKS_FOR_MS = 1200;

type Answer = boolean | null;
type SavedFeedback = NonNullable<ChatMessage['feedback']>;
type QuestionKey = 'understood' | 'correct' | 'helped';

const QUESTIONS: QuestionKey[] = ['understood', 'correct', 'helped'];

interface AssistantFeedbackProps {
  sessionId: string;
  messageId: string;
  feedback?: SavedFeedback;
  onSaved: (feedback: SavedFeedback) => void;
}

const deriveScore = (understood: Answer, correct: Answer, helped: Answer): number => {
  const answers = [understood, correct, helped].filter((value): value is boolean => value !== null);
  if (answers.length === 0) {
    return 0;
  }
  return answers.some((value) => !value) ? -1 : 1;
};

const AssistantFeedback: FC<AssistantFeedbackProps> = ({ sessionId, messageId, feedback, onSaved }) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const thanksTimer = useRef<ReturnType<typeof setTimeout>>();

  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState<Answer>(null);
  const [correct, setCorrect] = useState<Answer>(null);
  const [helped, setHelped] = useState<Answer>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const saved = !!feedback;
  const savedScore = feedback?.score ?? 0;
  const hasText = text.trim().length > 0;
  const hasAnswers = understood !== null || correct !== null || helped !== null;
  const canSend = !sending && !thanks && (hasAnswers || hasText);

  const answers: Record<QuestionKey, Answer> = { understood, correct, helped };
  const setters: Record<QuestionKey, Dispatch<SetStateAction<Answer>>> = {
    understood: setUnderstood,
    correct: setCorrect,
    helped: setHelped,
  };

  const close = useCallback(() => {
    clearTimeout(thanksTimer.current);
    setOpen(false);
    setThanks(false);
  }, []);

  const place = useCallback((popHeight: number) => {
    const button = buttonRef.current;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - 2 * EDGE_GAP);
    const rtl = getComputedStyle(button).direction === 'rtl';
    const left = Math.max(
      EDGE_GAP,
      Math.min(rtl ? rect.right - width : rect.left, window.innerWidth - width - EDGE_GAP),
    );
    const below = rect.bottom + EDGE_GAP;
    const top =
      below + popHeight <= window.innerHeight - EDGE_GAP
        ? below
        : Math.max(EDGE_GAP, rect.top - popHeight - EDGE_GAP);
    setPosition({ top, left });
  }, []);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    setUnderstood(feedback?.understood ?? null);
    setCorrect(feedback?.correct ?? null);
    setHelped(feedback?.helped ?? null);
    setText(feedback?.reason ?? '');
    setFailed(false);
    setThanks(false);
    place(POPOVER_ESTIMATED_HEIGHT);
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (open && popRef.current) {
      place(popRef.current.offsetHeight);
    }
  }, [open, failed, thanks, understood, correct, helped, place]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (popRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    const onScroll = (event: Event) => {
      if (!popRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  useEffect(() => () => clearTimeout(thanksTimer.current), []);

  const choose = (key: QuestionKey, value: boolean) => {
    setters[key]((current) => (current === value ? null : value));
  };

  const send = async () => {
    if (!canSend) {
      return;
    }
    const reason = text.trim();
    const score = deriveScore(understood, correct, helped);
    setSending(true);
    setFailed(false);
    try {
      await assistantService.sendAssistantFeedback(sessionId, {
        messageId,
        score,
        understood,
        correct,
        helped,
        reason,
      });
      onSaved({ score, understood, correct, helped, reason });
      setThanks(true);
      thanksTimer.current = setTimeout(close, THANKS_FOR_MS);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  const stateClass = savedScore > 0 ? ' is-up' : savedScore < 0 ? ' is-down' : saved ? ' is-note' : '';
  const icon = savedScore > 0 ? faThumbsUp : savedScore < 0 ? faThumbsDown : faCommentDots;
  const label = saved ? t('assistant.feedback.sent') : t('assistant.feedback.open');

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={'assistant-feedback-btn' + stateClass + (open ? ' is-open' : '')}
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <FontAwesomeIcon icon={icon} />
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            className="assistant-feedback-pop"
            role="dialog"
            aria-label={t('assistant.feedback.title')}
            style={{ top: position.top, left: position.left }}
          >
            <div className="assistant-feedback-pop-head">
              <strong>{t('assistant.feedback.title')}</strong>
              <button
                type="button"
                className="assistant-feedback-pop-close"
                title={t('assistant.feedback.close')}
                aria-label={t('assistant.feedback.close')}
                onClick={close}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="assistant-feedback-questions">
              {QUESTIONS.map((key) => (
                <div key={key} className="assistant-feedback-q">
                  <span className="assistant-feedback-q-label">{t(`assistant.feedback.q_${key}`)}</span>
                  <div className="assistant-feedback-q-btns" role="group" aria-label={t(`assistant.feedback.q_${key}`)}>
                    <button
                      type="button"
                      className={'assistant-feedback-q-btn is-yes' + (answers[key] === true ? ' is-active' : '')}
                      aria-pressed={answers[key] === true}
                      disabled={sending || thanks}
                      onClick={() => choose(key, true)}
                    >
                      {t('assistant.feedback.yes')}
                    </button>
                    <button
                      type="button"
                      className={'assistant-feedback-q-btn is-no' + (answers[key] === false ? ' is-active' : '')}
                      aria-pressed={answers[key] === false}
                      disabled={sending || thanks}
                      onClick={() => choose(key, false)}
                    >
                      {t('assistant.feedback.no')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <textarea
              className="assistant-feedback-text"
              dir="auto"
              rows={3}
              maxLength={MAX_TEXT_CHARS}
              value={text}
              placeholder={t('assistant.feedback.placeholder')}
              disabled={sending || thanks}
              onChange={(event) => setText(event.target.value)}
            />
            <div className="assistant-feedback-actions">
              <button type="button" className="assistant-feedback-send" disabled={!canSend} onClick={send}>
                {sending ? t('assistant.feedback.sending') : t('assistant.feedback.send')}
              </button>
            </div>
            {thanks && <div className="assistant-feedback-note is-ok">{t('assistant.feedback.thanks')}</div>}
            {failed && <div className="assistant-feedback-note is-error">{t('assistant.feedback.error')}</div>}
          </div>,
          document.body,
        )}
    </>
  );
};

export default AssistantFeedback;
